import { MOTION_LIMITS, stepSeconds } from './motion-limits.js';
import { isModelKind } from './models.js';
import { idsIn, movesIn, sceneStateAt } from './scene-state.js';
import { isShapeKind } from './shape-limits.js';
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isVec3 = (value) => Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === 'number');
/** 12.1 as "12,1" and 14 as "14". */
const dutchSeconds = (n) => String(Math.round(n * 10) / 10).replace('.', ',');
const LISTS = ['show', 'hide'];
export function assessMotion(document) {
    const problems = [];
    if (!isRecord(document))
        return problems;
    const add = (path, en, nl) => void problems.push({ path, en, nl });
    const objects = isRecord(document['objects']) ? document['objects'] : {};
    const steps = Array.isArray(document['steps']) ? document['steps'] : [];
    const vr = document['mode'] !== 'ar';
    const object = (id) => {
        const found = objects[id];
        return isRecord(found) ? found : undefined;
    };
    /** A shape placed with a `pos`: the only thing that can move, turn or scale. */
    const movable = (id) => {
        const found = object(id);
        return (found !== undefined &&
            typeof found['kind'] === 'string' &&
            isShapeKind(found['kind']) &&
            isVec3(found['pos']));
    };
    /** A library model: its rot, scale and move are the model rules' to say (models.ts). */
    const model = (id) => {
        const found = object(id);
        return found !== undefined && typeof found['kind'] === 'string' && isModelKind(found['kind']);
    };
    for (const [id, found] of Object.entries(objects)) {
        if (!isRecord(found))
            continue;
        const at = `/objects/${id}`;
        const turned = !model(id) && (found['rot'] !== undefined || found['scale'] !== undefined);
        if (turned && !movable(id)) {
            add(at, `"${id}" has rot or scale, which only a shape with a pos takes`, `"${id}": rot en scale kunnen alleen op een vorm met een pos`);
        }
        else if (turned && !vr) {
            // An AR assemble part is stood upright on its tray and snaps home
            // upright, so a declared turn would be lost; the catalogue says VR.
            add(at, `"${id}" has rot or scale, which only a vr lesson allows`, `"${id}": rot en scale werken alleen in VR`);
        }
        if (found['hidden'] === true) {
            if (!isVec3(found['pos'])) {
                add(`${at}/hidden`, `"${id}" is hidden, which needs an object with a pos`, `"${id}": hidden kan alleen op iets met een pos`);
            }
            if (!vr) {
                add(`${at}/hidden`, `"${id}" is hidden, which only a vr lesson allows`, `"${id}": hidden werkt alleen in VR`);
            }
        }
    }
    let moves = 0;
    steps.forEach((step, index) => {
        if (!isRecord(step))
            return;
        const at = `/steps/${index}`;
        const name = typeof step['id'] === 'string' ? step['id'] : String(index + 1);
        const inStep = `stap "${name}": `;
        const unknown = (path, what, id) => add(path, `${what} references unknown object "${id}"`, `${inStep}"${id}" bestaat niet`);
        for (const list of LISTS) {
            idsIn(step[list]).forEach((id, i) => {
                if (!object(id))
                    unknown(`${at}/${list}/${i}`, list, id);
            });
        }
        const stepMoves = movesIn(step['move']);
        moves += stepMoves.length;
        const moveList = Array.isArray(step['move']) ? step['move'] : [];
        for (const move of stepMoves) {
            const path = `${at}/move/${moveList.indexOf(move)}`;
            if (!object(move.object)) {
                unknown(`${path}/object`, 'move', move.object);
                continue;
            }
            if (!movable(move.object) && !model(move.object)) {
                add(`${path}/object`, `"${move.object}" cannot move: only a shape with a pos can`, `${inStep}"${move.object}" kan niet bewegen (alleen een vorm met een pos)`);
            }
            if (move['pos'] === undefined && move['rot'] === undefined && move['scale'] === undefined) {
                add(path, `the move of "${move.object}" changes nothing: give pos, rot or scale`, `${inStep}de beweging van "${move.object}" verandert niets (geef pos, rot of scale)`);
            }
        }
        const highlight = step['highlight'];
        if (typeof highlight === 'string' && !object(highlight)) {
            unknown(`${at}/highlight`, 'highlight', highlight);
        }
        const named = [
            ['show', idsIn(step['show'])],
            ['hide', idsIn(step['hide'])],
            ['move', stepMoves.map((move) => move.object)],
        ];
        for (const [list, ids] of named) {
            const seen = new Set();
            for (const id of ids) {
                if (seen.has(id)) {
                    add(`${at}/${list}`, `"${id}" is named twice in ${list}`, `${inStep}"${id}" staat twee keer in ${list}`);
                }
                seen.add(id);
            }
        }
        const hidden = new Set(idsIn(step['hide']));
        for (const id of new Set(idsIn(step['show']))) {
            if (hidden.has(id)) {
                add(at, `"${id}" is both shown and hidden in one step`, `${inStep}"${id}" wordt tegelijk getoond en verborgen`);
            }
        }
        if (!vr) {
            for (const field of ['show', 'hide', 'move', 'highlight']) {
                if (step[field] !== undefined) {
                    add(`${at}/${field}`, `${field} is for vr lessons only`, `${inStep}${field} werkt alleen in VR`);
                }
            }
        }
        // What a step is about must be there when the step's changes are done:
        // a step about a hidden thing is a blank step.
        const states = sceneStateAt(document, index);
        const gone = (id) => typeof id === 'string' && states.get(id)?.visible === false;
        const view = isRecord(step['view']) ? step['view'] : undefined;
        const subjects = new Set([step['focus'], view?.['look']].filter(gone));
        for (const id of subjects) {
            add(at, `"${id}" is what the step is about, but is hidden at its end`, `${inStep}"${id}" is dan verborgen maar is het onderwerp`);
        }
        if (gone(highlight)) {
            add(`${at}/highlight`, `"${highlight}" is highlighted, but is hidden at the end of the step`, `${inStep}"${highlight}" is dan verborgen maar wordt uitgelicht`);
        }
        const hotspots = Array.isArray(step['hotspots']) ? step['hotspots'] : [];
        hotspots.forEach((hotspot, h) => {
            const id = isRecord(hotspot) ? hotspot['object'] : undefined;
            if (gone(id)) {
                add(`${at}/hotspots/${h}/object`, `"${id}" has a hotspot, but is hidden at the end of the step`, `${inStep}"${id}" is dan verborgen maar heeft een hotspot`);
            }
        });
        const seconds = stepSeconds(step);
        if (seconds > MOTION_LIMITS.stepSeconds + 1e-9) {
            add(at, `the step's changes take ${dutchSeconds(seconds).replace(',', '.')} s; at most ${MOTION_LIMITS.stepSeconds}`, `${inStep}${dutchSeconds(seconds)} s beweging (hooguit ${MOTION_LIMITS.stepSeconds})`);
        }
    });
    if (moves > MOTION_LIMITS.movesPerLesson) {
        add('/steps', `${moves} moves; a lesson may have at most ${MOTION_LIMITS.movesPerLesson}`, `${moves} bewegingen (hooguit ${MOTION_LIMITS.movesPerLesson})`);
    }
    return problems;
}
//# sourceMappingURL=motion.js.map